export const DAY_MS = 24 * 60 * 60 * 1000;

export function addDays(from: Date, days: number): Date {
  return new Date(from.getTime() + days * DAY_MS);
}

/**
 * "2 days", "5 hours", "12 minutes", "any moment now". Used for every countdown
 * in the UI — auto-approval deadlines are shown to both parties everywhere, so
 * this needs to read naturally at every scale.
 */
export function formatDuration(ms: number): string {
  if (ms <= 0) return "any moment now";

  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return "less than a minute";
  if (minutes < 60) return plural(minutes, "minute");

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return plural(hours, "hour");

  const days = Math.round(hours / 24);
  return plural(days, "day");
}

export function formatCountdown(deadline: Date | string | null | undefined, now = new Date()) {
  if (!deadline) return null;
  const target = typeof deadline === "string" ? new Date(deadline) : deadline;
  const ms = target.getTime() - now.getTime();
  return { ms, overdue: ms <= 0, label: formatDuration(ms) };
}

function plural(value: number, unit: string): string {
  return `${value} ${unit}${value === 1 ? "" : "s"}`;
}

const dateFormatter = new Intl.DateTimeFormat("en-IN", {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "Asia/Kolkata",
});

const dateTimeFormatter = new Intl.DateTimeFormat("en-IN", {
  day: "numeric",
  month: "short",
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
  timeZone: "Asia/Kolkata",
});

const timeFormatter = new Intl.DateTimeFormat("en-IN", {
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
  timeZone: "Asia/Kolkata",
});

/**
 * Assemble from formatToParts so Node ICU and Chromium cannot disagree on
 * dayPeriod casing ("am"/"AM") or the date–time separator (", "/" at ").
 */
function part(parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes): string {
  return parts.find((entry) => entry.type === type)?.value ?? "";
}

function formatClock(parts: Intl.DateTimeFormatPart[]): string {
  const hour = part(parts, "hour");
  const minute = part(parts, "minute");
  const dayPeriod = part(parts, "dayPeriod").toUpperCase();
  return `${hour}:${minute} ${dayPeriod}`;
}

/** `deliver_by` is a DATE column; pg returns it as a "YYYY-MM-DD" string. */
export function formatDate(value: Date | string): string {
  const date = typeof value === "string" ? parseDateOnly(value) : value;
  return dateFormatter.format(date);
}

export function formatDateTime(value: Date | string): string {
  const date = typeof value === "string" ? new Date(value) : value;
  const parts = dateTimeFormatter.formatToParts(date);
  return `${part(parts, "day")} ${part(parts, "month")}, ${formatClock(parts)}`;
}

export function formatTime(value: Date | string): string {
  const date = typeof value === "string" ? new Date(value) : value;
  return formatClock(timeFormatter.formatToParts(date));
}

const dayKeyFormatter = new Intl.DateTimeFormat("en-CA", {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  timeZone: "Asia/Kolkata",
});

/** Stable IST day identifier, for grouping a thread into days. */
export function dayKey(value: Date | string): string {
  return dayKeyFormatter.format(typeof value === "string" ? new Date(value) : value);
}

/** "Today", "Yesterday", or a date. Used as thread day separators. */
export function formatDayLabel(value: Date | string, now: Date = new Date()): string {
  const key = dayKey(value);
  if (key === dayKey(now)) return "Today";
  if (key === dayKey(new Date(now.getTime() - DAY_MS))) return "Yesterday";
  return formatDate(typeof value === "string" ? new Date(value) : value);
}

/** Inbox-style stamp: the clock time today, then "Yesterday", then a date. */
export function formatInboxStamp(value: Date | string, now: Date = new Date()): string {
  const date = typeof value === "string" ? new Date(value) : value;
  const key = dayKey(date);
  if (key === dayKey(now)) return formatTime(date);
  if (key === dayKey(new Date(now.getTime() - DAY_MS))) return "Yesterday";
  return formatDate(date);
}

function parseDateOnly(value: string): Date {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T00:00:00+05:30`) : new Date(value);
}

export function toDateOnly(date: Date): string {
  return date.toISOString().slice(0, 10);
}
